import React, { useMemo, useState } from "react";
import Icon from "./Icon";
import { formatEnglishDate } from "../../utils/dateFormatting";
import "./certification.css";

const typeConfig = {
  DOCUMENT: { icon: "file-text", color: "#6B7280", label: "Document" },
  AUDIT: { icon: "clipboard-check", color: "#3B82F6", label: "Audit" },
  CERTIFICATION: { icon: "award", color: "#10B981", label: "Certification" },
  INSPECTION: { icon: "search", color: "#8B5CF6", label: "Inspection" },
};

const formatRelative = (dateString) => {
  if (!dateString) return "-";
  const diff = Date.now() - new Date(dateString).getTime();
  const days = Math.round(diff / (1000 * 60 * 60 * 24));
  if (days < 1) return "today";
  if (days < 30) return `${days} days ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} months ago`;
  const years = Math.round(months / 12);
  return `${years} years ago`;
};

const isEvidenceVerified = (item) => {
  if (!item || typeof item !== "object") return false;
  if (item.isVerified === true) return true;
  if (item.verificationStatus === "VERIFIED") return true;
  // Legacy payload fallback: evidence list historically contained verified-only items.
  if (item.isVerified == null && item.verificationStatus == null) return true;
  return false;
};

const getEvidenceTitle = (item) => {
  const questionText = typeof item?.questionText === "string" ? item.questionText.trim() : "";
  if (questionText) return questionText;

  const title = typeof item?.title === "string" ? item.title.trim() : "";
  if (title) return title;

  return "Verified evidence";
};

const getVerifiedDate = (item) => item?.verifiedAt || item?.verifiedDate || null;

const EvidenceList = ({ evidence, variant = "compact", privacySafe = false }) => {
  const [filterType, setFilterType] = useState("ALL");
  const [expanded, setExpanded] = useState({});
  const [showAll, setShowAll] = useState(false);

  const normalizedEvidence = useMemo(() => {
    const list = Array.isArray(evidence) ? [...evidence] : [];
    return privacySafe ? list.filter(isEvidenceVerified) : list;
  }, [evidence, privacySafe]);

  const sortedEvidence = useMemo(() => {
    const list = [...normalizedEvidence];
    list.sort((a, b) => new Date(getVerifiedDate(b) || 0) - new Date(getVerifiedDate(a) || 0));
    return list;
  }, [normalizedEvidence]);

  const filteredEvidence = useMemo(() => {
    if (privacySafe) return sortedEvidence;
    if (filterType === "ALL") return sortedEvidence;
    return sortedEvidence.filter((item) => item.type === filterType);
  }, [sortedEvidence, filterType, privacySafe]);

  if (!normalizedEvidence.length) {
    return <div className="cert-empty">No verified evidence available</div>;
  }

  const visibleEvidence = variant === "compact" && !showAll ? filteredEvidence.slice(0, 3) : filteredEvidence;
  const hiddenCount = filteredEvidence.length - visibleEvidence.length;

  return (
    <div className="cert-evidence">
      {!privacySafe && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <div>
            <label htmlFor="evidenceFilter" style={{ fontSize: "0.85rem", color: "#6b7280" }}>
              Filter by type
            </label>
            <select
              id="evidenceFilter"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={{ marginLeft: 8, padding: "6px 10px", borderRadius: 8, border: "1px solid #d1d5db" }}
            >
              <option value="ALL">All</option>
              {Object.keys(typeConfig).map((type) => (
                <option key={type} value={type}>
                  {typeConfig[type].label}
                </option>
              ))}
            </select>
          </div>
          {variant === "compact" && hiddenCount > 0 && (
            <button className="cert-evidence-toggle" onClick={() => setShowAll((prev) => !prev)}>
              {showAll ? "Show less" : `Show ${hiddenCount} more`}
            </button>
          )}
        </div>
      )}

      {visibleEvidence.map((item) => {
        const config = typeConfig[item.type] || typeConfig.DOCUMENT;
        const isVerified = isEvidenceVerified(item);
        const evidenceDate = getVerifiedDate(item);
        const evidenceTitle = privacySafe ? getEvidenceTitle(item) : item.description;
        const isLong = !privacySafe && item.description && item.description.length > 120;
        const isExpanded = expanded[item.evidenceId];

        if (variant === "compact") {
          return (
            <div key={item.evidenceId} className="cert-evidence-compact">
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Icon name={config.icon} size={20} color={config.color} />
                <div>
                  <div className="cert-evidence-type">{evidenceTitle}</div>
                  {evidenceDate && <div className="cert-evidence-meta">Verified on {formatEnglishDate(evidenceDate)}</div>}
                </div>
              </div>
              <span className="cert-evidence-badge">{isVerified ? "Verified" : "Not verified"}</span>
            </div>
          );
        }

        return (
          <div key={item.evidenceId} className="cert-evidence-card">
            <Icon name={config.icon} size={32} color={config.color} />
            <div style={{ flex: 1 }}>
              <div className="cert-evidence-type">{privacySafe ? evidenceTitle : config.label}</div>
              {!privacySafe && (
                <>
                  <div className="cert-evidence-description">
                    {isLong && !isExpanded ? `${item.description.slice(0, 120)}...` : item.description}
                  </div>
                  {isLong && (
                    <button
                      className="cert-evidence-toggle"
                      onClick={() =>
                        setExpanded((prev) => ({
                          ...prev,
                          [item.evidenceId]: !prev[item.evidenceId],
                        }))
                      }
                    >
                      {isExpanded ? "Collapse" : "Expand"}
                    </button>
                  )}
                </>
              )}
              {evidenceDate && (
                <div className="cert-evidence-meta">
                  {privacySafe
                    ? `Verified on ${formatEnglishDate(evidenceDate)}`
                    : `Verified on ${formatEnglishDate(evidenceDate)} · ${formatRelative(evidenceDate)}`}
                </div>
              )}
            </div>
            <span className="cert-evidence-badge">{isVerified ? "Verified" : "Not verified"}</span>
          </div>
        );
      })}
    </div>
  );
};

export default EvidenceList;
